/**
 * Represents a single line in an SDP (Session Description Protocol) message.
 * Each line consists of a type indicator and its corresponding value.
 */
type SdpLine = {
  type: string;
  value: string;
};

/**
 * Represents a media section within an SDP message.
 * Contains the media type and all associated SDP lines for that media.
 */
type MediaSection = {
  type: string;
  lines: SdpLine[];
};

/**
 * Represents the complete parsed structure of an SDP message.
 * Separates session-level attributes from media-specific sections.
 */
type SdpSection = {
  session: SdpLine[];
  media: MediaSection[];
};

/**
 * Configuration interface for controlling SDP processing behavior.
 * Allows customization of codec enforcement, debugging, and call analysis.
 */
interface SdpConfig {
  debug: boolean;
  enforceAudioCodecs: string[];
  preferredAudioCodec: string;
  enableExternalCallDetection: boolean;
}

/**
 * SDP (Session Description Protocol) service for WebRTC call management.
 *
 * This singleton service provides comprehensive SDP manipulation capabilities including:
 * - PCMU codec enforcement for external calls
 * - Audio codec prioritization and filtering
 * - External call detection based on URI patterns
 * - SDP parsing and reconstruction
 *
 * The service is designed to ensure optimal audio quality and compatibility
 * for both internal (person-to-person) and external (PSTN) calls.
 */
export class SdpService {
  private static instance: SdpService;
  private config: SdpConfig;

  /**
   * Private constructor to enforce singleton pattern.
   * Initializes the service with default configuration optimized for PCMU codec preference.
   */
  private constructor() {
    this.config = {
      debug: false,
      enforceAudioCodecs: ['PCMU'],
      preferredAudioCodec: 'PCMU',
      enableExternalCallDetection: true,
    };
  }

  /**
   * Gets the singleton instance of the SdpService.
   * Creates a new instance if one doesn't exist.
   *
   * @returns The singleton SdpService instance
   */
  static getInstance(): SdpService {
    if (!SdpService.instance) {
      SdpService.instance = new SdpService();
    }
    return SdpService.instance;
  }

  /**
   * Updates the service configuration with new settings.
   * Only the provided properties will be updated; others remain unchanged.
   *
   * @param newConfig - Partial configuration object with properties to update
   *
   * @example
   * ```typescript
   * sdpService.updateConfig({
   *   debug: true,
   *   preferredAudioCodec: 'G722'
   * });
   * ```
   */
  updateConfig(newConfig: Partial<SdpConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Removes OPUS and G722 codecs from SDP and prioritizes PCMU codec.
   *
   * This method processes WebRTC session descriptions to enforce PCMU codec usage
   * by removing competing codecs (OPUS, G722) and ensuring PCMU appears first
   * in the codec preference list. This is essential for maintaining compatibility
   * with external PSTN networks.
   *
   * @param description - The RTCSessionDescriptionInit object to modify
   * @param options - Configuration options for the codec modification
   * @param options.onlyForLocalDescriptions - If true, only process offers (local descriptions)
   * @param options.debug - Enable debug logging for this operation
   *
   * @returns Modified RTCSessionDescriptionInit with PCMU prioritized
   *
   * @example
   * ```typescript
   * const offer = await peerConnection.createOffer();
   * const modifiedOffer = sdpService.removeOpusCodecAndPrioritizePcmu(offer, {
   *   onlyForLocalDescriptions: true,
   *   debug: true
   * });
   * await peerConnection.setLocalDescription(modifiedOffer);
   * ```
   */
  removeOpusCodecAndPrioritizePcmu(
    description: RTCSessionDescriptionInit,
    options: { onlyForLocalDescriptions?: boolean; debug?: boolean } = {}
  ): RTCSessionDescriptionInit {
    const debug = options.debug ?? this.config.debug;

    if (!description?.sdp) {
      return description;
    }

    // If this flag is set, only process offers (local descriptions)
    if (options.onlyForLocalDescriptions && description.type !== 'offer') {
      if (debug) {
        console.log(
          '[SDP] Skipping codec modification for non-offer description:',
          description.type
        );
      }
      return description;
    }

    try {
      const parsedSdp = this.parseSdp(description.sdp);
      let modified = false;

      if (debug) {
        console.log('[SDP] Processing description type:', description.type);
      }

      for (const mediaSection of parsedSdp.media) {
        if (mediaSection.type !== 'audio') {
          continue;
        }

        // Get all available codecs
        const availableCodecs = this.getAvailableCodecs(mediaSection);
        if (debug) {
          console.log(
            '[SDP] Available codecs before modification:',
            availableCodecs
          );
        }

        // Find OPUS codec to remove
        const opusLine = mediaSection.lines.find(
          (line) =>
            line.type === 'a' &&
            line.value.startsWith('rtpmap:') &&
            line.value.includes('opus/')
        );

        // Find existing PCMU codec
        const existingPcmuLine = mediaSection.lines.find(
          (line) =>
            line.type === 'a' &&
            line.value.startsWith('rtpmap:') &&
            line.value.includes('PCMU/')
        );

        // Remove OPUS codec if present
        if (opusLine) {
          const opusPayloadType = opusLine.value.split(':')[1].split(' ')[0];
          if (debug) {
            console.log(
              '[SDP] Removing OPUS codec with payload type:',
              opusPayloadType
            );
          }

          // Remove OPUS codec lines
          mediaSection.lines = mediaSection.lines.filter((line) => {
            if (line.type !== 'a') {
              return true;
            }

            return !(
              line.value.startsWith(`rtpmap:${opusPayloadType}`) ||
              line.value.startsWith(`fmtp:${opusPayloadType}`) ||
              line.value.startsWith(`rtcp-fb:${opusPayloadType}`)
            );
          });

          // Update media line to remove OPUS payload type
          const mediaLine = mediaSection.lines.find(
            (line) => line.type === 'm'
          );
          if (mediaLine) {
            const parts = mediaLine.value.split(' ');
            const filteredPayloads = parts
              .slice(3)
              .filter((p) => p !== opusPayloadType);
            mediaLine.value = [...parts.slice(0, 3), ...filteredPayloads].join(
              ' '
            );
          }

          modified = true;
        }

        // Remove G722 codec to force PCMU usage
        const g722Line = mediaSection.lines.find(
          (line) =>
            line.type === 'a' &&
            line.value.startsWith('rtpmap:') &&
            line.value.includes('G722/')
        );

        if (g722Line) {
          const g722PayloadType = g722Line.value.split(':')[1].split(' ')[0];
          if (debug) {
            console.log(
              '[SDP] Removing G722 codec with payload type:',
              g722PayloadType
            );
          }

          // Remove G722 codec lines
          mediaSection.lines = mediaSection.lines.filter((line) => {
            if (line.type !== 'a') {
              return true;
            }

            return !(
              line.value.startsWith(`rtpmap:${g722PayloadType}`) ||
              line.value.startsWith(`fmtp:${g722PayloadType}`) ||
              line.value.startsWith(`rtcp-fb:${g722PayloadType}`)
            );
          });

          // Update media line to remove G722 payload type
          const mediaLine = mediaSection.lines.find(
            (line) => line.type === 'm'
          );
          if (mediaLine) {
            const parts = mediaLine.value.split(' ');
            const filteredPayloads = parts
              .slice(3)
              .filter((p) => p !== g722PayloadType);
            mediaLine.value = [...parts.slice(0, 3), ...filteredPayloads].join(
              ' '
            );
          }

          modified = true;
        }

        // ALWAYS ensure PCMU is present and first
        const mediaLine = mediaSection.lines.find((line) => line.type === 'm');
        if (mediaLine) {
          const parts = mediaLine.value.split(' ');
          const payloads = parts.slice(3);

          // If PCMU doesn't exist, add it
          if (!existingPcmuLine) {
            if (debug) {
              console.log('[SDP] Adding PCMU codec (payload type 0)');
            }
            // Add PCMU rtpmap line
            mediaSection.lines.push({
              type: 'a',
              value: 'rtpmap:0 PCMU/8000',
            });
          }

          // Always ensure PCMU (payload type 0) is first in the list
          const otherPayloads = payloads.filter((p) => p !== '0');
          const newPayloads = ['0', ...otherPayloads];

          // Update media line with PCMU first
          mediaLine.value = [...parts.slice(0, 3), ...newPayloads].join(' ');

          if (debug) {
            console.log('[SDP] Updated payload order:', newPayloads);
          }
          modified = true;
        }

        // Log final codecs after modification
        if (debug) {
          const finalCodecs = this.getAvailableCodecs(mediaSection);
          console.log(
            '[SDP] Available codecs after modification:',
            finalCodecs
          );
        }
      }

      if (!modified) {
        return description;
      }

      return {
        ...description,
        sdp: this.writeSdp(parsedSdp),
      };
    } catch (err) {
      console.error('[SDP] Error during codec modification:', err);
      return description;
    }
  }

  /**
   * Asynchronous wrapper for PCMU codec enforcement specifically for local descriptions.
   *
   * This method provides a Promise-based interface for codec modification,
   * automatically filtering to only process local descriptions (offers).
   * It's designed to be used in async/await contexts for better integration
   * with modern WebRTC workflows.
   *
   * @param description - The RTCSessionDescriptionInit object to modify
   * @param options - Configuration options
   * @param options.debug - Enable debug logging for this operation
   *
   * @returns Promise that resolves to the modified RTCSessionDescriptionInit
   *
   * @example
   * ```typescript
   * const offer = await peerConnection.createOffer();
   * const modifiedOffer = await sdpService.pcmuEnforcementModifier(offer);
   * await peerConnection.setLocalDescription(modifiedOffer);
   * ```
   */
  async pcmuEnforcementModifier(
    description: RTCSessionDescriptionInit,
    options: { debug?: boolean } = {}
  ): Promise<RTCSessionDescriptionInit> {
    const debug = options.debug ?? this.config.debug;

    if (!description || !description.type || !description.sdp) {
      throw new Error('[SDP Modifier] Invalid input SDP');
    }

    if (debug) {
      console.log(
        '[SDP Modifier] Before:',
        description.type,
        description.sdp?.slice(0, 80)
      );
    }

    if (description.type === 'pranswer' || description.type === 'answer') {
      const out = { type: description.type, sdp: description.sdp };
      if (debug) {
        console.log('[SDP Modifier] After:', out.type, out.sdp?.slice(0, 80));
      }
      return out;
    }

    const result = this.removeOpusCodecAndPrioritizePcmu(description, {
      onlyForLocalDescriptions: true,
      debug,
    });

    const out = {
      type: result.type || description.type,
      sdp: result.sdp || description.sdp,
    };

    if (debug) {
      console.log('[SDP Modifier] After:', out.type, out.sdp?.slice(0, 80));
    }

    return out;
  }

  /**
   * Analyzes call parameters to determine if the call is external (PSTN) or internal.
   *
   * This method uses multiple heuristics to detect external calls:
   * 1. CallUri containing 'pr' indicates internal (person-to-person) calls
   * 2. Phone numbers with 7+ digits indicate external PSTN calls
   * 3. SIP URIs with numeric user parts suggest external calls
   * 4. Multiple URI analysis for comprehensive detection
   *
   * External call detection is important for applying appropriate codec policies,
   * as external calls often require PCMU codec for PSTN compatibility.
   *
   * @param callUri - The primary call URI (optional)
   * @param phoneNumber - The phone number being called (optional)
   * @param sipFromUri - The SIP FROM header URI (optional)
   * @param sipToUri - The SIP TO header URI (optional)
   * @param debug - Enable debug logging for this analysis (optional)
   *
   * @returns True if the call is determined to be external, false for internal calls
   *
   * @example
   * ```typescript
   * // Internal call example
   * const isExternal1 = sdpService.analyzeIfCallIsExternal(
   *   'sip:user123pr@domain.com'
   * ); // Returns false
   *
   * // External call example
   * const isExternal2 = sdpService.analyzeIfCallIsExternal(
   *   'sip:15551234567@domain.com',
   *   '+1 (555) 123-4567'
   * ); // Returns true
   * ```
   */
  analyzeIfCallIsExternal(
    callUri?: string,
    phoneNumber?: string,
    sipFromUri?: string,
    sipToUri?: string,
    debug?: boolean
  ): boolean {
    const useDebug = debug ?? this.config.debug;

    if (!this.config.enableExternalCallDetection) {
      if (useDebug) {
        console.log('[SDP] External call detection is disabled');
      }
      return false;
    }

    // PRIMARY CHECK: CallUri containing 'pr' indicates internal call (person-to-person)
    if (callUri && callUri.includes('pr')) {
      if (useDebug) {
        console.log(
          '🏢 SIP Analysis: Internal call detected - callUri contains "pr"'
        );
      }
      return false;
    }

    // SECONDARY CHECK: If phoneNumber is populated, it typically indicates external call
    if (phoneNumber && phoneNumber.trim()) {
      // Remove formatting characters and check if only digits remain
      // eslint-disable-next-line no-useless-escape
      const cleanNumber = phoneNumber.replace(/[\s\-\(\)+]/g, '');
      if (/^\d{7,}$/.test(cleanNumber)) {
        // 7+ digits indicates external phone number (PSTN)
        if (useDebug) {
          console.log(
            'SIP Analysis: External call detected - phoneNumber with 7+ digits'
          );
        }
        return true;
      }
    }

    // TERTIARY CHECK: CallUri with numeric user part indicates external call
    if (callUri) {
      const uriMatch = callUri.match(/sip:([^@]+)@/i);
      if (uriMatch) {
        // eslint-disable-next-line no-useless-escape
        const userPart = uriMatch[1].replace(/[\s\-\(\)+]/g, '');
        if (/^\d{7,}$/.test(userPart)) {
          // 7+ digits in URI user part indicates external number
          if (useDebug) {
            console.log(
              'SIP Analysis: External call detected - callUri with 7+ digit number'
            );
          }
          return true;
        }
      }
    }

    // QUATERNARY CHECK: SIP From/To URIs with long numeric sequences indicate external calls
    const sipUris = [sipFromUri, sipToUri].filter(Boolean);
    for (const uri of sipUris) {
      const uriMatch = uri.match(/sip:([^@]+)@/i);
      if (uriMatch) {
        // eslint-disable-next-line no-useless-escape
        const userPart = uriMatch[1].replace(/[\s\-\(\)+]/g, '');
        if (/^\d{7,}$/.test(userPart)) {
          // 7+ digits in SIP URI indicates external number
          if (useDebug) {
            console.log(
              'SIP Analysis: External call detected - SIP URI with 7+ digit number'
            );
          }
          return true;
        }
      }
    }

    // Default: Assume internal call if no external indicators found
    if (useDebug) {
      console.log('SIP Analysis: Defaulting to internal call');
    }
    return false;
  }

  /**
   * Parses an SDP string into a structured format for easy manipulation.
   *
   * This method converts the text-based SDP format into a hierarchical structure
   * that separates session-level attributes from media-specific sections.
   * The parsed format facilitates programmatic modification of SDP content.
   *
   * @param sdp - The SDP string to parse (RFC 4566 format)
   * @returns Structured representation of the SDP content
   *
   * @private
   * @throws {Error} When SDP format is invalid or cannot be parsed
   */
  private parseSdp(sdp: string): SdpSection {
    const lines = sdp.split('\r\n').filter((line) => line.trim() !== '');
    const session: SdpLine[] = [];
    const media: MediaSection[] = [];

    let currentMedia: MediaSection | null = null;

    for (const line of lines) {
      if (line.startsWith('m=')) {
        if (currentMedia) {
          media.push(currentMedia);
        }

        const mediaType = line.split(' ')[0].substring(2);
        currentMedia = {
          type: mediaType,
          lines: [{ type: 'm', value: line.substring(2) }],
        };
      } else if (currentMedia) {
        const [type, ...valueParts] = line.split('=');
        currentMedia.lines.push({ type, value: valueParts.join('=') });
      } else {
        const [type, ...valueParts] = line.split('=');
        session.push({ type, value: valueParts.join('=') });
      }
    }

    if (currentMedia) {
      media.push(currentMedia);
    }

    return { session, media };
  }

  /**
   * Converts a parsed SDP structure back into RFC 4566 compliant string format.
   *
   * This method reconstructs the text-based SDP format from the structured
   * representation, ensuring proper line ordering and formatting according
   * to RFC 4566 specifications.
   *
   * @param parsedSdp - The structured SDP data to convert back to string format
   * @returns RFC 4566 compliant SDP string with proper line endings
   *
   * @private
   */
  private writeSdp(parsedSdp: SdpSection): string {
    const lines: string[] = [];

    for (const line of parsedSdp.session) {
      lines.push(`${line.type}=${line.value}`);
    }

    for (const mediaSection of parsedSdp.media) {
      const mediaLine = mediaSection.lines.find((line) => line.type === 'm');
      if (mediaLine) {
        lines.push(`m=${mediaLine.value}`);
      }

      for (const line of mediaSection.lines) {
        if (line.type !== 'm') {
          lines.push(`${line.type}=${line.value}`);
        }
      }
    }

    return lines.join('\r\n') + '\r\n';
  }

  /**
   * Extracts and formats available codec information from a media section.
   *
   * This helper method scans through SDP attributes to identify all available
   * codecs and their payload types, returning them in a readable format
   * for debugging and analysis purposes.
   *
   * @param mediaSection - The media section to analyze for codec information
   * @returns Array of codec descriptions in "payloadType:codecName" format
   *
   * @private
   *
   * @example
   * Return format: ["0:PCMU/8000", "8:PCMA/8000", "101:telephone-event/8000"]
   */
  private getAvailableCodecs(mediaSection: MediaSection): string[] {
    const codecs: string[] = [];

    for (const line of mediaSection.lines) {
      if (line.type === 'a' && line.value.startsWith('rtpmap:')) {
        const payloadType = line.value.split(':')[1].split(' ')[0];
        const codecName = line.value.split(' ')[1];
        codecs.push(`${payloadType}:${codecName}`);
      }
    }

    return codecs;
  }
}

/**
 * Default singleton instance of SdpService for convenient access.
 *
 * @example
 * ```typescript
 * import sdpService from './voipSdpService';
 *
 * const isExternal = sdpService.analyzeIfCallIsExternal(callUri);
 * ```
 */
export default SdpService.getInstance();
